import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/footer';
import Scrollbar from '../../components/scrollbar';
import Logo from '../../images/logo.svg';
import SEO from '../../SEO';
import { createPayment } from '../../api/flow';
import './PaymentPage.scss';

const PaymentPage = () => {
  const navigate = useNavigate();
  const [params, setParams] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const hasRedirected = useRef(false);

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const data = Object.fromEntries(search.entries());

    if (!data.uid || !data.email) {
      setError(
        'Faltan datos de la reserva. Por favor, intenta agendar nuevamente.',
      );
      return;
    }

    setParams(data);
  }, []);

  // Protección: advertir si intentan salir de la página
  useEffect(() => {
    if (!params || redirecting) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue =
        'Si abandonas esta página, tu reserva será cancelada por falta de pago. ¿Estás seguro de que deseas salir?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [params, redirecting]);

  // Redirección automática al pago
  useEffect(() => {
    if (!params || hasRedirected.current || redirecting) return;

    const redirectToPayment = async () => {
      if (hasRedirected.current) return;

      setRedirecting(true);
      setLoading(true);

      try {
        const paymentData = {
          bookingUid: params.uid,
          email: params.email,
          subject: params.title || 'Asesoría Jurídica',
        };

        const response = await createPayment(paymentData);

        if (response.checkoutUrl) {
          hasRedirected.current = true;
          // Redirigir al checkout de Flow después de 2 segundos
          setTimeout(() => {
            window.location.href = response.checkoutUrl;
          }, 2000);
        } else {
          throw new Error(response.error || 'No se recibió enlace de pago');
        }
      } catch (err) {
        console.error('Error al crear pago:', err);
        setError(
          err.response?.data?.message ||
            'Ocurrió un problema al generar el pago. Por favor, intenta nuevamente o contáctanos.',
        );
        setRedirecting(false);
        setLoading(false);
        hasRedirected.current = false;
      }
    };

    // Redirigir automáticamente después de 1 segundo
    const timer = setTimeout(() => {
      redirectToPayment();
    }, 1000);

    return () => clearTimeout(timer);
  }, [params, redirecting]);

  if (error && !params) {
    return (
      <div className="payment-page-wrapper">
        <SEO
          title="Error - Reserva"
          description="Error al procesar la reserva"
          canonical="https://estudiohya.cl/payment"
        />
        <Navbar
          hclass={'wpo-header-style-2'}
          Logo={Logo}
          topbarNone={'header-without-topbar'}
        />
        <div className="payment-page">
          <div className="container">
            <div className="payment-container">
              <div className="error-message">
                <div className="error-icon">⚠️</div>
                <h2>Error al procesar la reserva</h2>
                <p>{error}</p>
                <button
                  className="btn-primary"
                  onClick={() => navigate('/asesoria')}
                >
                  Volver a Agendar
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <Scrollbar />
      </div>
    );
  }

  if (!params) {
    return (
      <div className="payment-page-wrapper">
        <SEO
          title="Procesando Reserva"
          description="Cargando información de la reserva"
          canonical="https://estudiohya.cl/payment"
        />
        <Navbar
          hclass={'wpo-header-style-2'}
          Logo={Logo}
          topbarNone={'header-without-topbar'}
        />
        <div className="payment-page">
          <div className="container">
            <div className="payment-container">
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Cargando información de la reserva...</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <Scrollbar />
      </div>
    );
  }

  return (
    <div className="payment-page-wrapper">
      <SEO
        title="Confirmar Pago - Asesoría Jurídica"
        description="Confirma tu reserva y procede con el pago seguro"
        canonical="https://estudiohya.cl/payment"
      />
      <Navbar
        hclass={'wpo-header-style-2'}
        Logo={Logo}
        topbarNone={'header-without-topbar'}
      />

      <div className="payment-page">
        <div className="container">
          <div className="payment-container">
            {redirecting || loading ? (
              <div className="redirecting-state">
                <div className="spinner"></div>
                <h2>Redirigiendo al Portal de Pago...</h2>
                <p>Serás redirigido automáticamente en unos segundos.</p>
              </div>
            ) : error ? (
              <div className="error-message">
                <div className="error-icon">⚠️</div>
                <h2>Error al procesar el pago</h2>
                <p>{error}</p>
                <button
                  className="btn-primary"
                  onClick={() => navigate('/asesoria')}
                >
                  Volver a Agendar
                </button>
              </div>
            ) : (
              <div className="redirecting-state">
                <div className="spinner"></div>
                <h2>Preparando tu pago...</h2>
                <p>
                  Por favor espera mientras generamos tu enlace de pago seguro.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
      <Scrollbar />
    </div>
  );
};

export default PaymentPage;
